import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from 'prop-types';
import { Form, Button, Spinner } from 'react-bootstrap';
import { validate } from "../services/helpers";
import AlertDialog from "./AlertDialog";

const messages = defineMessages({
    login: {
        id: "Kirjaudu MyRopo-palveluun",
        defaultMessage: "Kirjaudu MyRopo-palveluun"
    },
    getStarted: {
        id: "Aloitetaan!",
        defaultMessage: "Aloitetaan!"
    },
    infoFirst: {
        id: "MyRopoon kirjautuminen on nopeaa ja helppoa – syötä vain sähköpostiosoitteesi ja käytä vahvistuskoodia, jonka lähetämme sinulle. Vahva tunnistautuminen tarvitaan vain kerran; sen jälkeen voit kirjautua helposti ja turvallisesti samoilla tunnuksilla.",
        defaultMessage: "MyRopoon kirjautuminen on nopeaa ja helppoa – syötä vain sähköpostiosoitteesi ja käytä vahvistuskoodia, jonka lähetämme sinulle. Vahva tunnistautuminen tarvitaan vain kerran; sen jälkeen voit kirjautua helposti ja turvallisesti samoilla tunnuksilla.",
    },
    infoSecond: {
        id: "Käyttäjätunnuksen luomiseksi pyydämme lupaa tallentaa henkilötietosi. Jos et halua antaa lupaa, voit aina kirjautua kertaluonteisella %vahvalla tunnistautumisella%.",
        defaultMessage: "Käyttäjätunnuksen luomiseksi pyydämme lupaa tallentaa henkilötietosi. Jos et halua antaa lupaa, voit aina kirjautua kertaluonteisella %vahvalla tunnistautumisella%."
    },
    emailLabel: {
        id: "Sähköposti",
        defaultMessage: "Sähköposti"
    },
    emailValidationError: {
        id: "Syötä kelvollinen sähköposti, esim. nimi@esimerkki.com",
        defaultMessage: "Syötä kelvollinen sähköposti, esim. nimi@esimerkki.com"
    },
    rememberMeToggleLabel: {
        id: "Muista sähköpostiosoite tässä selaimessa",
        defaultMessage: "Muista sähköpostiosoite tässä selaimessa"
    },
    continue: {
        id: "Jatka",
        defaultMessage: "Jatka"
    }
});

const SignInEmail = (props) => {
    const lng = props.intl.formatMessage;
    const defaultEmail = localStorage.getItem('defaultEmail');
    const [email, setEmail] = useState((defaultEmail?.length ?? 0) > 1 ? defaultEmail : '');
    const [rememberUser, setRememberUser] = useState(defaultEmail !== '-');

    /**
     * Handle email input change; validate the input
     * and update the email state.
     *
     * @param {*} event
     */
    const handleEmail = (event) => {
        // remove validation errors
        if (validate(event.target)) {
            event.target.classList.remove("is-invalid");
        }
        setEmail(event.target?.value?.toLocaleLowerCase() || '');
    }

    /**
     * Handle remember me toggle; update the rememberUser state.
     * @param remember
     */
    const handleRememberMe = (remember) => {
        setRememberUser(remember);
        if (!remember) {
            localStorage.setItem('defaultEmail', '-');
        }
    }

    /**
     * Handle form submission with passed function.
     *
     * @param {*} event
     */
    const handleSubmit = (event) => {
        event.preventDefault();

        // check input validity and continue with the sign-in process if ok
        if (validate(event.currentTarget)) {
            props.handleSubmitEmail(email, rememberUser);
        }
    }

    /**
     * Parse the instructions to include a link to identity verification page.
     * @returns {*[]}
     */
    const parseInstructions = (txt) => {
        const matches = txt.match(/%.+%/gm);
        if (!matches || matches.length < 1) {
            return txt;
        }
        const match = matches[0];
        const parts = txt.split(match);
        const elements = [];
        for (let i = 0; i < parts.length; i++) {
            elements.push(parts[i]);
            if (i === 0) {
                elements.push(<a href={props.strongIdentificationUrl}>{match.substring(1, match.length - 1)}</a>);
            }
        }
        return elements;
    }

    return (
        <>
            <h2 className={'mb-0'}>{lng(messages.getStarted)}</h2>
            <div>
                <p>{lng(messages.infoFirst)}</p>
                <p>{parseInstructions(lng(messages.infoSecond))}</p>
            </div>
            <Form id="email-user-sign-in-form" aria-label={lng(messages.login)} className="w-100" onSubmit={handleSubmit} noValidate>
                {props.alert && (
                    <AlertDialog
                        type={props.alert.type ?? 'danger'}
                        message={props.alert.message}
                    />
                )}
                <Form.Floating className="mb-2 pb-2">
                    <Form.Control
                        id="user-email"
                        autoComplete="off"
                        autoCorrect="off"
                        type="email"
                        value={email}
                        placeholder={lng(messages.emailLabel)}
                        validationmessage={lng(messages.emailValidationError)}
                        onChange={(e) => handleEmail(e)}
                        required
                    />
                    <Form.Label htmlFor="user-email">{lng(messages.emailLabel)}</Form.Label>
                    <Form.Control.Feedback type="invalid" />
                </Form.Floating>
                <Form.Group controlId="rememberMe">
                    <Form.Check
                        type="switch"
                        id="email-remember-me"
                        label={lng(messages.rememberMeToggleLabel)}
                        checked={rememberUser}
                        onChange={(e) => handleRememberMe(e.target.checked)}
                    />
                </Form.Group>
            </Form>
            <Button
                form="email-user-sign-in-form"
                variant="primary"
                type="submit"
                aria-label={lng(messages.continue)}
                disabled={props.isSubmitting}
            >
                {props.isSubmitting && (<Spinner animation="border" size="sm" />)} {lng(messages.continue)}
            </Button>
        </>
    )
};

SignInEmail.propTypes = {
    'intl': PropTypes.object.isRequired,
    'handleSubmitEmail': PropTypes.func.isRequired,
    'isSubmitting': PropTypes.bool,
    'alert': PropTypes.object,
    'strongIdentificationUrl': PropTypes.string.isRequired
};

export default withRouter(injectIntl(SignInEmail));
